import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { NextSeo } from 'next-seo';
import { DEFAULT_SEO_CONFIG, ROOT_URL } from 'next-seo.config';

import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { PortfolioTabs } from '@/components/Portfolio/PortfolioTabs';

import { getCachedWalletAddresses } from '@/utils/cachedWalletAddresses';
import { parseProfileView } from '@/utils/profileRoutes';

const PROFILE_PAGE_TITLE = 'Profile | Matcha DEX aggregator';
const PROFILE_PAGE_DESCRIPTION =
  'View your token balances across all supported chains. Track your portfolio value in real-time with Matcha.';

function ProfilePage({ view }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const canonicalUrl = `${ROOT_URL}/profile`;

  return (
    <>
      <NextSeo
        {...DEFAULT_SEO_CONFIG}
        title={PROFILE_PAGE_TITLE}
        description={PROFILE_PAGE_DESCRIPTION}
        canonical={canonicalUrl}
        openGraph={{
          title: PROFILE_PAGE_TITLE,
          description: PROFILE_PAGE_DESCRIPTION,
          images: [
            {
              url: `${ROOT_URL}/images/og/social-banner.jpg`,
              width: 1200,
              height: 630,
              alt: 'Matcha',
            },
          ],
        }}
        additionalMetaTags={[
          {
            name: 'twitter:title',
            content: PROFILE_PAGE_TITLE,
          },
          {
            name: 'twitter:description',
            content: PROFILE_PAGE_DESCRIPTION,
          },
          {
            name: 'twitter:image',
            content: `${ROOT_URL}/images/og/social-banner.jpg`,
          },
        ]}
      />
      <Header />
      <PortfolioTabs view={view} />
      <Footer />
    </>
  );
}

export const getServerSideProps = (async (context) => {
  const { query } = context;
  const view = parseProfileView(query.tab);
  if (!view) return { notFound: true };

  return {
    props: {
      view,
      cachedWalletAddresses: getCachedWalletAddresses(context),
    },
  };
}) satisfies GetServerSideProps;

export default ProfilePage;
